import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as d3 from 'd3';
import type { BranchColorRule, GraphDTO, ThemePreference } from '@clearflow/shared';
import { api } from '../../lib/api.ts';
import { buildGitgraphModel } from './buildGitgraphModel.ts';
import {
  computeLayout,
  computeBubble,
  DOT_R,
  LINE_H,
  BUBBLE_PAD_Y,
  DASH_H,
  LABEL_H,
  LABEL_CHAR_W,
  LABEL_PAD_X,
  TAG_H,
  TAG_CHAR_W,
  TAG_PAD_X,
} from './computeLayout.ts';
import type { Layout, LayoutBranchLabel, LayoutEdge, LayoutNode, TruncatedRoot } from './computeLayout.ts';
import { useClampedMenuPosition } from './useClampedMenuPosition.ts';
import { CommitCiSection } from './CommitCiSection.tsx';

interface Props {
  repoId: string;
  graph: GraphDTO;
  selectedBranches: Set<string>;
  rangeStartSha?: string;
  rangeEndSha?: string;
  onCommitContextMenu: (sha: string, event: MouseEvent) => void;
  onTagContextMenu: (tagName: string, event: MouseEvent) => void;
  selectedCherryPickId?: string;
  focusedCommitSha?: string;
  // Recentre et zoome sur le label de cette branche (voir l'effet dédié plus bas), déclenché par
  // un clic sur son item dans BranchFilter.tsx (hors case à cocher). Même convention que
  // focusedCommitSha ci-dessus : ne se re-déclenche pas si on reçoit deux fois le même nom
  // d'affilée (deps de useEffect), cohérent avec la limite déjà acceptée pour focusedCommitSha.
  focusedBranchName?: string;
  onVisibleShas?: (shas: Set<string>) => void;
  // Survol d'un lien cherry-pick dans le graphe — le parent (RepoViewPage) résout id → cp/branches
  // (il a déjà toutes les maps nécessaires pour la même tooltip montée au survol de la sidebar) et
  // affiche CherryPickTooltip ancrée sur (x, y), le curseur. null au survol de rien/autre chose.
  onCherryPickHover?: (hover: { id: string; x: number; y: number } | null) => void;
  // Which cherry-pick link (if any) should render highlighted — driven by the parent's tooltip
  // state (cpTooltip in RepoViewPage), not by raw hover here: see the .classed('cp-hover', ...)
  // comment in renderGraph for why the highlight must track the tooltip's lifecycle rather than
  // the hitarea's mouseenter/mouseleave directly.
  highlightedCherryPickId?: string | null;
  // Le menu contextuel de branche (BranchContextMenu, ouvert au clic) vit dans le parent
  // (RepoViewPage), pas dans ce composant.
  onBranchContextMenu: (branch: BranchInfo, event: MouseEvent) => void;
  // Le menu contextuel ouvert au clic sur un dot (CommitContextMenu) vit lui aussi dans le
  // parent — sans ce callback, la tooltip de commit au survol n'a aucun moyen de le fermer.
  onCommitHover?: () => void;
  // Ouvre la modal « Configuration CI » (lien « Configurer » de la tooltip) ; absent = pas de lien.
  onConfigureCi?: () => void;
  // Position/zoom à restaurer au premier chargement, à la place du fit-to-view par défaut —
  // undefined tant que le parent n'a pas encore chargé la préférence (le composant attend plutôt
  // que de fit-to-view puis se corriger, cf. isFirstFit plus bas), null pour dire explicitement
  // "rien à restaurer, fit-to-view normal".
  initialView?: { scrollLeft: number; scrollTop: number; scale: number } | null;
  // Notifié (debounced) à chaque pan/zoom pour que le parent persiste la position par compte —
  // jamais à chaque scroll/wheel event, ce serait un PATCH par pixel.
  onViewChange?: (view: { scrollLeft: number; scrollTop: number; scale: number }) => void;
  // Seul le canvas de la minimap en a besoin explicitement (voir cssVar/MINIMAP_VIEWPORT_OVERLAY
  // plus haut) — le reste du graphe (SVG) suit tout seul via var(--...) dans ses attributs de
  // présentation, pas besoin de re-render au changement de thème.
  theme: ThemePreference;
  // Partagées par dépôt (Repository.branchColorRules), éditées depuis RepoViewPage — voir
  // computeLayout.ts, branchColor(). [] par défaut : un dépôt sans règle garde la palette
  // habituelle.
  branchColorRules?: BranchColorRule[];
  // URL de base Jira déjà résolue côté serveur (JiraAccessDTO.jiraBaseUrl) — pour transformer les
  // clés de ticket d'un trailer "Refs: GU-XX" en pastilles cliquables dans la tooltip de commit
  // (voir renderCommitBody). null si aucune URL Jira n'est configurée : le texte reste alors
  // affiché tel quel, sans essayer de deviner un lien.
  jiraBaseUrl?: string | null;
}

export interface BranchInfo {
  name: string;
  color: string;
  originSha?: string;
  isDefault?: boolean;
  isProtected?: boolean;
  isMerged?: boolean;
}

// Exportée pour CommitSearchBar.tsx, qui colore son icône de résultat "tag" avec la même
// couleur que les rects de tag dans le graphe — évite que les deux dérivent l'une de l'autre.
export const TAG_COLOR = '#e8b84b';

type TooltipState = {
  text: string;
  sha: string;
  authorName: string;
  authoredAt: number;
  x: number;
  y: number;
  // Épinglée par un clic sur la bulle (ou sur l'icône dédiée dans la tooltip) — reste affichée
  // quel que soit le survol ailleurs dans le graphe ou une sortie du conteneur, pour laisser le
  // temps d'aller cliquer un bouton dedans (copier le SHA…). Se referme par un nouveau clic sur la
  // même bulle/icône, ou n'importe où ailleurs dans le graphe.
  pinned: boolean;
} | null;

function formatDate(ts: number): string {
  return new Date(ts * 1000).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}

// Même motif que extractTicketKey côté service (apps/api/src/services/jira.ts).
const TICKET_KEY_RE = /[A-Z][A-Z0-9_]+-\d+/g;

// Pastille "Refs:" de la tooltip de commit — composant à part (plutôt qu'un <a> statique) car sa
// couleur dépend du statut du ticket (vert si Done, comme .jira-ticket-badge-done dans la sidebar
// Tickets Jira), connu seulement après un getIssue complet. Même queryKey que JiraTicketTooltip/
// RepoJiraTickets (['jira-ticket-detail', ticketKey]) : si le ticket a déjà été survolé ailleurs
// dans l'app, ce fetch ressert le cache (staleTime 15s) au lieu de le refaire.
function CommitRefTicketPill({ ticketKey, jiraBaseUrl }: { ticketKey: string; jiraBaseUrl: string }) {
  const { data } = useQuery({
    queryKey: ['jira-ticket-detail', ticketKey],
    queryFn: () => api.ticketDetail(ticketKey),
    staleTime: 15_000,
  });
  return (
    <a
      className={`jira-ticket-badge${data?.isDone ? ' jira-ticket-badge-done' : ''}`}
      href={`${jiraBaseUrl}/browse/${ticketKey}`}
      target="_blank"
      rel="noreferrer"
      title={`Ouvrir ${ticketKey} dans un nouvel onglet`}
    >
      {ticketKey}
    </a>
  );
}

// Transforme les clés de ticket d'un trailer "Refs: GU-XX, GU-YY" (convention de commit du projet
// — voir CLAUDE.md) en pastilles cliquables (même style .jira-ticket-badge que la sidebar Tickets
// Jira), sans toucher au reste du message. Seule la ligne "Refs:" est concernée, espaces
// d'indentation ignorés en tête de ligne (matching ET affichage — les trailers de ce projet sont
// indentés de 2 espaces comme le reste des listes à puces d'un message de commit, ex.
// "  Refs: GU-315", mais rien n'oblige à reproduire cette indentation une fois rendu) — une clé
// qui apparaîtrait ailleurs dans le sujet/corps (rare, mais possible dans un message de commit
// libre) n'est pas un trailer et reste du texte brut, pour ne pas transformer n'importe quelle
// suite de lettres majuscules-tiret-chiffres en lien à tort. Sans jiraBaseUrl connue (pas de
// token/URL Jira configuré), impossible de construire un lien valide : le texte est alors laissé
// tel quel plutôt que de pointer vers du vide.
function renderCommitBody(text: string, jiraBaseUrl: string | null | undefined): ReactNode {
  if (!jiraBaseUrl) return text;
  const nodes: ReactNode[] = [];
  text.split('\n').forEach((line, lineIndex) => {
    if (lineIndex > 0) nodes.push('\n');
    const refsMatch = /^\s*(Refs:\s*)(.*)$/.exec(line);
    if (!refsMatch) {
      nodes.push(line);
      return;
    }
    nodes.push(refsMatch[1]);
    const rest = refsMatch[2];
    const re = new RegExp(TICKET_KEY_RE);
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(rest))) {
      if (match.index > lastIndex) nodes.push(rest.slice(lastIndex, match.index));
      const key = match[0];
      nodes.push(<CommitRefTicketPill key={`${lineIndex}-${match.index}`} ticketKey={key} jiraBaseUrl={jiraBaseUrl} />);
      lastIndex = re.lastIndex;
    }
    if (lastIndex < rest.length) nodes.push(rest.slice(lastIndex));
  });
  return nodes;
}

const PIPELINE_STATUS_CLASS: Record<string, string> = {
  success: 'badge-pipeline-success',
  failed: 'badge-pipeline-failed',
  running: 'badge-pipeline-running',
  pending: 'badge-pipeline-pending',
  created: 'badge-pipeline-pending',
  waiting_for_resource: 'badge-pipeline-pending',
  preparing: 'badge-pipeline-pending',
  scheduled: 'badge-pipeline-pending',
  canceled: 'badge-pipeline-neutral',
  skipped: 'badge-pipeline-neutral',
  manual: 'badge-pipeline-neutral',
};

export function pipelineBadgeClass(status: string): string {
  return PIPELINE_STATUS_CLASS[status] ?? 'badge-pipeline-neutral';
}

const STAGE_ICON_COLOR: Record<string, string> = {
  success: 'var(--success)',
  success_with_warnings: 'var(--warning)',
  failed: 'var(--danger)',
  running: 'var(--info)',
  pending: 'var(--warning)',
  created: 'var(--warning)',
  waiting_for_resource: 'var(--warning)',
  preparing: 'var(--warning)',
  scheduled: 'var(--warning)',
  canceled: 'var(--muted)',
  skipped: 'var(--muted)',
  manual: 'var(--muted)',
};

function stageIconColor(status: string): string {
  return STAGE_ICON_COLOR[status] ?? 'var(--muted)';
}

// Small stage-status glyph modeled after GitLab's pipeline "Stages" column icons.
// Exportée pour BranchContextMenu.tsx, qui affiche les mêmes icônes de stages.
export function StageIcon({ name, status }: { name: string; status: string }) {
  const color = stageIconColor(status);
  let glyph: ReactNode;
  if (status === 'running') {
    glyph = (
      <circle
        cx="7" cy="7" r="5.5" fill="none" stroke={color} strokeWidth="2"
        strokeDasharray="26" strokeDashoffset="19.5" strokeLinecap="round"
      />
    );
  } else if (status === 'success') {
    glyph = (
      <>
        <circle cx="7" cy="7" r="6" fill={color} />
        <path d="M4 7.2 L6.2 9.3 L10 4.9" stroke="var(--panel-2)" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </>
    );
  } else if (status === 'success_with_warnings') {
    // Exact path data from GitLab's own icon sprite (@gitlab/svgs, symbol "status_warning",
    // viewBox 0 0 14 14 — same coordinate space as every glyph in this component) rather than a
    // hand-drawn approximation, per explicit ask: this is the ring+"!" GitLab shows for a stage
    // that passed but contains an allow_failure job that actually failed.
    glyph = (
      <>
        <path
          fill={color}
          d="M7 0a7 7 0 1 1 0 14A7 7 0 0 1 7 0zm0 1a6 6 0 1 0 0 12A6 6 0 0 0 7 1z"
        />
        <path
          fill={color}
          d="M6 3.5c0-.3.2-.5.5-.5h1c.3 0 .5.2.5.5v4c0 .3-.2.5-.5.5h-1c-.3 0-.5-.2-.5-.5v-4m0 6c0-.3.2-.5.5-.5h1c.3 0 .5.2.5.5v1c0 .3-.2.5-.5.5h-1c-.3 0-.5-.2-.5-.5v-1"
        />
      </>
    );
  } else if (status === 'failed') {
    glyph = (
      <>
        <circle cx="7" cy="7" r="6" fill={color} />
        <path d="M4.5 4.5 L9.5 9.5 M9.5 4.5 L4.5 9.5" stroke="var(--panel-2)" strokeWidth="1.4" strokeLinecap="round" />
      </>
    );
  } else if (status === 'canceled') {
    glyph = (
      <>
        <circle cx="7" cy="7" r="5.5" fill="none" stroke={color} strokeWidth="1.4" />
        <path d="M4.5 4.5 L9.5 9.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
      </>
    );
  } else if (status === 'skipped') {
    glyph = <circle cx="7" cy="7" r="5.5" fill="none" stroke={color} strokeWidth="1.4" strokeDasharray="2,2" />;
  } else {
    // pending / created / waiting_for_resource / preparing / scheduled / manual / unknown
    glyph = (
      <>
        <circle cx="7" cy="7" r="5.5" fill="none" stroke={color} strokeWidth="1.4" />
        <circle cx="7" cy="7" r="1.3" fill={color} />
      </>
    );
  }
  return (
    <span className="stage-icon-wrap" title={`${name}: ${status}`}>
      <svg
        width="14" height="14" viewBox="0 0 14 14"
        className={status === 'running' ? 'stage-icon stage-icon-running' : 'stage-icon'}
      >
        {glyph}
      </svg>
    </span>
  );
}

// ── D3 rendering ─────────────────────────────────────────────────────────────

const RANGE_START_COLOR  = 'var(--success)';
const RANGE_EDGE_COLOR   = '#e8b84b';

// Résout une variable CSS en couleur concrète (hex/rgb) — nécessaire là où d3 ne peut pas
// utiliser var() directement : le canvas de la minimap (fillStyle/strokeStyle n'interprètent
// pas var()) et les .transition() de couleur (d3-interpolate ne sait interpoler que des couleurs
// déjà résolues, pas la chaîne littérale "var(--x)"). Les .attr(...) D3 "normaux" sur des
// éléments SVG n'ont pas ce problème — ce sont des attributs de présentation, donc var() y est
// résolu par le navigateur comme en CSS, et se met à jour tout seul si le thème change.
function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

// Survol du minimap (rectangle représentant le viewport) — seule pièce du graphe qui ne peut
// pas simplement passer par cssVar() : ces deux nuances n'ont pas d'équivalent parmi les tokens
// --overlay-* déjà définis pour le reste de l'appli.
const MINIMAP_VIEWPORT_OVERLAY: Record<ThemePreference, { fill: string; stroke: string }> = {
  dark:  { fill: 'rgba(255,255,255,0.07)', stroke: 'rgba(255,255,255,0.45)' },
  light: { fill: 'rgba(0,0,0,0.06)',       stroke: 'rgba(0,0,0,0.35)' },
};

// ── Minimap drawing (extrait de drawMinimap, S3776 : complexité cognitive) ──────────────────────
// Chaque fonction dessine une couche indépendante sur le même ctx, dans l'ordre où drawMinimap les
// appelle — pur découpage, aucune logique déplacée entre les couches.

function drawMinimapLaneLines(ctx: CanvasRenderingContext2D, nodes: LayoutNode[], sy: number, W: number) {
  const seenY = new Map<number, string>();
  for (const node of nodes) {
    if (!seenY.has(node.y)) seenY.set(node.y, node.color);
  }
  for (const [y, color] of seenY) {
    ctx.strokeStyle = color + '28';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, y * sy);
    ctx.lineTo(W, y * sy);
    ctx.stroke();
  }
}

// Retourne false sans rien dessiner si le path n'a pas au moins 2 points exploitables — au appelant
// de poser strokeStyle/lineWidth avant, chaque couche voulant un style différent pour le même tracé.
function strokeMinimapEdgePath(ctx: CanvasRenderingContext2D, edge: LayoutEdge, sx: number, sy: number): void {
  const pts = [...edge.path.matchAll(/[ML]\s+(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g)].map(
    (m) => ({ x: Number.parseFloat(m[1]) * sx, y: Number.parseFloat(m[2]) * sy }),
  );
  if (pts.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.stroke();
}

function drawMinimapEdges(ctx: CanvasRenderingContext2D, edges: LayoutEdge[], sx: number, sy: number) {
  for (const edge of edges) {
    ctx.strokeStyle = edge.color + (edge.isMerge ? 'cc' : '88');
    ctx.lineWidth = edge.isMerge ? 1 : 0.75;
    strokeMinimapEdgePath(ctx, edge, sx, sy);
  }
}

// Redessine par-dessus les edges de la branche survolée, en plus épais/plein, pour reproduire dans
// le minimap le .branch-hover appliqué dans le graphe principal.
function drawMinimapHoveredBranch(
  ctx: CanvasRenderingContext2D, edges: LayoutEdge[], hoveredBranchName: string, sx: number, sy: number,
) {
  ctx.lineWidth = 2;
  for (const edge of edges) {
    if (edge.branchName !== hoveredBranchName) continue;
    ctx.strokeStyle = edge.color;
    strokeMinimapEdgePath(ctx, edge, sx, sy);
  }
}

// Liens cherry-pick (pointillés, couleur de la branche source comme dans le graphe principal).
function drawMinimapCherryPicks(
  ctx: CanvasRenderingContext2D,
  cherryPicks: { sourceSha: string; targetSha: string }[],
  nodeMap: Map<string, LayoutNode>,
  sx: number,
  sy: number,
) {
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 2]);
  for (const cp of cherryPicks) {
    const src = nodeMap.get(cp.sourceSha);
    const tgt = nodeMap.get(cp.targetSha);
    if (!src || !tgt) continue;
    ctx.strokeStyle = src.color;
    // Même forme (coude en Z) que le graphe principal — cherryPickPoints partagé.
    const [first, ...rest] = cherryPickPoints(src, tgt);
    ctx.beginPath();
    ctx.moveTo(first.x * sx, first.y * sy);
    for (const pt of rest) ctx.lineTo(pt.x * sx, pt.y * sy);
    ctx.stroke();
  }
  ctx.setLineDash([]);
}

function minimapDotRadius(isDotHovered: boolean, isBranchHovered: boolean): number {
  if (isDotHovered) return 3;
  if (isBranchHovered) return 2.2;
  return 1.5;
}

// Dots de commit — agrandis pour la branche survolée (branch-hover) ou pour le seul dot survolé
// (dot-hover, cerclé de blanc), miroir du .branch-hover/.dot-hover du graphe SVG. Un commit tagué
// reçoit en plus un halo TAG_COLOR — rayon distinct du halo de hover pour que les deux restent
// lisibles si un même commit est à la fois tagué et survolé.
function drawMinimapDots(
  ctx: CanvasRenderingContext2D,
  nodes: LayoutNode[],
  shaToTags: Map<string, string[]>,
  hoveredBranchName: string | null,
  hoveredDotSha: string | null,
  sx: number,
  sy: number,
) {
  for (const node of nodes) {
    const isDotHovered = node.sha === hoveredDotSha;
    const isBranchHovered = !!hoveredBranchName && node.branchName === hoveredBranchName;
    const isTagged = shaToTags.has(node.sha);
    const r = minimapDotRadius(isDotHovered, isBranchHovered);
    ctx.fillStyle = node.color;
    ctx.beginPath();
    ctx.arc(node.x * sx, node.y * sy, r, 0, Math.PI * 2);
    ctx.fill();
    if (isTagged) {
      ctx.strokeStyle = TAG_COLOR;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(node.x * sx, node.y * sy, r + 1.3, 0, Math.PI * 2);
      ctx.stroke();
    }
    if (isDotHovered) {
      ctx.strokeStyle = cssVar('--text');
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(node.x * sx, node.y * sy, r + (isTagged ? 2.6 : 0), 0, Math.PI * 2);
      ctx.stroke();
    }
  }
}

// Rectangle de viewport — clampé à un demi-pixel à l'intérieur des bords du canvas pour que le
// trait de 1px (centré sur le path) ne soit jamais coupé quand le rect touche un bord, ex. scrollé
// tout en bas ou tout à droite.
function drawMinimapViewportRect(
  ctx: CanvasRenderingContext2D,
  container: HTMLDivElement,
  k: number,
  sx: number,
  sy: number,
  W: number,
  H: number,
  overlay: { fill: string; stroke: string },
) {
  const vx = (container.scrollLeft / k) * sx;
  const vy = (container.scrollTop  / k) * sy;
  const vw = (container.clientWidth  / k) * sx;
  const vh = (container.clientHeight / k) * sy;
  const half = 0.5;
  const rx0 = Math.max(half, vx);
  const ry0 = Math.max(half, vy);
  const rx1 = Math.min(W - half, vx + vw);
  const ry1 = Math.min(H - half, vy + vh);

  ctx.fillStyle = overlay.fill;
  ctx.fillRect(rx0, ry0, rx1 - rx0, ry1 - ry0);
  ctx.strokeStyle = overlay.stroke;
  ctx.lineWidth = 1;
  ctx.strokeRect(rx0, ry0, rx1 - rx0, ry1 - ry0);
}

function computeRangeEdgeIds(
  commits: { sha: string; parents: string[] }[],
  startSha: string | undefined,
  endSha: string | undefined,
): Set<string> {
  if (!startSha || !endSha || startSha === endSha) return new Set();
  const map = new Map(commits.map((c) => [c.sha, c]));
  const ids = new Set<string>();
  let cur = endSha;
  for (let i = 0; i < 10_000; i++) {
    const c = map.get(cur);
    if (!c || c.parents.length === 0) return new Set();
    const p = c.parents[0];
    ids.add(`${p}-${cur}`);
    if (p === startSha) return ids;
    cur = p;
  }
  return new Set();
}

// Pulls the terminal point of an SVG path back by r pixels along the last segment,
// so an arrowhead marker placed at the path end sits on the dot's surface, not its center.
function pullBack(path: string, r: number): string {
  const re = /([ML])\s+([-\d.]+)\s+([-\d.]+)/g;
  const ms = [...path.matchAll(re)];
  if (ms.length < 2) return path;
  const last = ms[ms.length - 1];
  const prev = ms[ms.length - 2];
  const dx = +last[2] - +prev[2];
  const dy = +last[3] - +prev[3];
  const len = Math.hypot(dx, dy);
  if (len <= r) return path;
  const nx = +last[2] - r * dx / len;
  const ny = +last[3] - r * dy / len;
  return path.slice(0, last.index) + `${last[1]} ${nx.toFixed(2)} ${ny.toFixed(2)}`;
}

// Geometry of the <marker id="graph-arrow"> defined in GitGraph's JSX below — kept here as
// shared constants (not hardcoded twice) because ARROW_MARKER_LEN drifted out of sync with
// the marker's actual footprint twice already when only the JSX side got hand-tuned, each
// time silently breaking the pipeline-active cap/pulse split (see its usage further down).
const ARROW_TIP_X = 28;    // polygon's point, in marker-local units (= markerWidth)
const ARROW_REF_X = 20;    // refX — the marker-local x that aligns with the path's own endpoint
const ARROW_HEIGHT = 14;   // markerHeight, and the polygon's base height
// Polygon's flat back edge, in marker-local units. The underlying line (stroke-width up to 6,
// the widest a path carrying this marker ever uses) is drawn all the way to ARROW_REF_X, so the
// triangle must already be at least as wide as that stroke by the time it reaches ARROW_REF_X —
// otherwise its tapering sides fall inside the line's own edges before the line actually stops,
// letting the line's straight sides poke out past the triangle (a visible notch right before the
// tip). A plain triangle can't taper at a fixed rate from far back AND stay wide enough that
// close to the tip, so the base sits close to ARROW_REF_X instead of near ARROW_TIP_X: half-height
// at ARROW_REF_X is (ARROW_HEIGHT/2) * (ARROW_TIP_X-ARROW_REF_X)/(ARROW_TIP_X-ARROW_BASE_X) — with
// 18 that's 7*8/10 = 5.6px, comfortably over the 3px (half of 6) it needs to cover. Trade-off:
// vs. a base near ARROW_TIP_X's far side, the arrowhead reads a bit shorter/stubbier rather than
// elongated — ARROW_HEIGHT (its width, the more visually obvious dimension) is untouched.
const ARROW_BASE_X = 18;
// Distance, in real path units, from the path's endpoint back to the arrowhead's visible
// base. Not simply markerWidth: refX doesn't sit at the tip here (the tip overshoots refX
// by ARROW_TIP_X - ARROW_REF_X), and the base isn't at local x=0 either.
const ARROW_MARKER_LEN = ARROW_REF_X - ARROW_BASE_X;
// pullBack(path, r) below puts the path's own endpoint r units from whatever it's pulling
// back from — but a marker's visible TIP sits ARROW_TIP_X - ARROW_REF_X further out than
// that endpoint (see ARROW_MARKER_LEN above). So a path whose arrow tip must land exactly on
// a circle of radius r (e.g. a dot's outline, not its center) needs pullBack(path, r +
// ARROW_TIP_OVERSHOOT), not pullBack(path, r) — the latter only gets the raw endpoint there,
// leaving the tip itself overshooting into the circle by this much.
const ARROW_TIP_OVERSHOOT = ARROW_TIP_X - ARROW_REF_X;

// Path geometry for a cherry-pick link between two dots — factored out of renderGraph so the
// invisible hover hitarea (path.cherry-pick-hitarea) can share the exact same shape as the
// visible dashed line instead of drifting out of sync with a duplicated computation.
function cherryPickPoints(src: { x: number; y: number }, tgt: { x: number; y: number }): { x: number; y: number }[] {
  const { x: sx, y: sy } = src;
  const { x: tx, y: ty } = tgt;
  if (sy === ty) return [src, tgt];
  // Same elbow shape regardless of which side the target's lane is on:
  // a slope-4 diagonal in, a horizontal middle run, a slope-4 diagonal out.
  // dir tracks which way the target actually sits — the layout pass
  // (computeLayout.ts, cherry-pick Phase 3a/3b) pushes hard to keep tx > sx,
  // but a fully boxed-in case can still leave a residual gap, even a
  // negative one (target left of source). A plain 2-point line for that
  // case reads as the link running backward across the graph; breaking it
  // into the same diagonal/flat/diagonal Z-shape either way — just mirrored
  // by dir — keeps every segment at the intended ±4 slope regardless of
  // overall direction, same idea as the merge-edge elbow (75929b7c/2ed2154c).
  const dy = Math.abs(sy - ty);
  const dx = tx - sx;
  const dir = dx >= 0 ? 1 : -1;
  if (dx < 0 || dx > dy / 4) {
    const seg = dy / 8;
    const midY = (sy + ty) / 2;
    return [src, { x: sx + dir * seg, y: midY }, { x: tx - dir * seg, y: midY }, tgt];
  }
  return [src, tgt];
}

function cherryPickPath(src: { x: number; y: number }, tgt: { x: number; y: number }): string {
  const p = 'M ' + cherryPickPoints(src, tgt).map((pt) => `${pt.x} ${pt.y}`).join(' L ');
  return pullBack(p, DOT_R + ARROW_TIP_OVERSHOOT);
}

function renderGraph(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  layout: Layout,
  onCommitContextMenu: Props['onCommitContextMenu'],
  onTagContextMenu: Props['onTagContextMenu'],
  rangeStartSha?: string,
  rangeEndSha?: string,
  rangeEdgeIds: Set<string> = new Set(),
  cherryPicks: Array<{ id: string; sourceSha: string; targetSha: string }> = [],
  shaToTags: Map<string, string[]> = new Map(),
  selectedCherryPickId?: string,
  summarizedShas: Set<string> = new Set(),
  activePipelineBranches: Set<string> = new Set(),
  highlightedCherryPickId?: string | null,
  packageShas: Set<string> = new Set(),
) {
  // ── Edges ──
  const hasRange = rangeEdgeIds.size > 0;
  g.selectAll<SVGPathElement, typeof layout.edges[0]>('path.edge')
    .data(layout.edges, (d) => d.id)
    .join('path')
    .attr('class', 'edge')
    .classed('edge-pipeline-active', (d) => activePipelineBranches.has(d.branchName))
    .attr('d', (d) => d.isMerge ? pullBack(d.path, DOT_R + ARROW_TIP_OVERSHOOT) : d.path)
    .attr('stroke', (d) => rangeEdgeIds.has(d.id) ? RANGE_EDGE_COLOR : d.color)
    .attr('stroke-width', (d) => rangeEdgeIds.has(d.id) ? 6 : 5)
    .attr('stroke-opacity', (d) => hasRange && !rangeEdgeIds.has(d.id) ? 0.2 : 1)
    .attr('fill', 'none')
    .attr('marker-end', (d) => d.isMerge ? 'url(#graph-arrow)' : null)
    .attr('pointer-events', 'none')
    // A branch toggled off then back on re-enters this join; without a "next" edge
    // sibling already in the DOM to insert before, D3 falls back to appendChild,
    // dropping it after commit groups (on top of dots/bubbles) instead of underneath.
    // .lower() re-asserts "always behind" on every render, regardless of enter order.
    .lower();

  // Invisible wide stroke on top of each edge for easy hover detection.
  // Rendered before commit groups so dots (drawn later) capture events first when overlapping.
  g.selectAll<SVGPathElement, LayoutEdge>('path.edge-hitarea')
    .data(layout.edges, (d) => d.id)
    .join('path')
    .attr('class', 'edge-hitarea')
    .attr('d', (d) => d.path)
    .attr('stroke', 'transparent')
    .attr('stroke-width', 10)
    .attr('fill', 'none')
    .attr('pointer-events', 'stroke')
    .style('cursor', 'pointer')
    .lower(); // same re-entry gotcha as path.edge above

  // ── Commit groups ──
  const commits = g
    .selectAll<SVGGElement, typeof layout.nodes[0]>('g.commit')
    .data(layout.nodes, (d) => d.sha)
    .join('g')
    .attr('class', 'commit')
    .attr('pointer-events', 'none'); // events are set explicitly per child element

  // Dot — clic pour le menu contextuel ; le survol (mise en valeur du seul dot, pas de sa
  // branche) est géré par setDotHover dans le useEffect wheel/drag, pas par un
  // .on('mouseenter'...) local ici — même délégation par mousemove que edges/tips/bubble.
  commits
    .selectAll<SVGCircleElement, unknown>('circle.dot')
    .data((d) => [d])
    .join('circle')
    .attr('class', 'dot')
    .attr('cx', (d) => d.x)
    .attr('cy', (d) => d.y)
    .attr('r', DOT_R)
    .attr('fill', (d) => d.color)
    .attr('stroke', 'var(--bg)')
    .attr('stroke-width', 1.5)
    .attr('pointer-events', 'visiblePainted')
    .style('cursor', 'pointer')
    .on('click', (event: MouseEvent, d) => onCommitContextMenu(d.sha, event));

  // Anneau discret pour les commits déjà résumés individuellement — concentrique au
  // dot, ne prend pas de place en X donc n'interagit pas avec les passes anti-collision
  // des bulles/tags/labels.
  commits
    .selectAll<SVGCircleElement, unknown>('circle.dot-summarized-ring')
    .data((d) => (summarizedShas.has(d.sha) ? [d] : []))
    .join('circle')
    .attr('class', 'dot-summarized-ring')
    .attr('cx', (d) => d.x)
    .attr('cy', (d) => d.y)
    .attr('r', DOT_R + 3)
    .attr('fill', 'none')
    .attr('stroke', (d) => d.color)
    .attr('stroke-width', 1.2)
    .attr('stroke-opacity', 0.85)
    .attr('pointer-events', 'none');

  // Anneau « a produit un package » (GU-380) : concentrique au dot, hors layout comme l'anneau ci-dessus.
  // Tireté pour le distinguer de l'anneau plein « résumé » (les deux peuvent coexister : rayon plus grand).
  // Présence seulement : l'absence d'anneau ne dit rien (voir PackageCommitsResponse).
  commits
    .selectAll<SVGCircleElement, unknown>('circle.dot-package-ring')
    .data((d) => (packageShas.has(d.sha) ? [d] : []))
    .join('circle')
    .attr('class', 'dot-package-ring')
    .attr('cx', (d) => d.x)
    .attr('cy', (d) => d.y)
    .attr('r', DOT_R + 6)
    .attr('fill', 'none')
    .attr('stroke', 'var(--accent)')
    .attr('stroke-width', 1.6)
    .attr('stroke-dasharray', '3,2')
    .attr('pointer-events', 'none');

  // Inner specular highlight — small bright ellipse offset to top-left
  commits
    .selectAll<SVGEllipseElement, unknown>('ellipse.dot-shine')
    .data((d) => [d])
    .join('ellipse')
    .attr('class', 'dot-shine')
    .attr('cx', (d) => d.x - DOT_R * 0.28)
    .attr('cy', (d) => d.y - DOT_R * 0.28)
    .attr('rx', DOT_R * 0.38)
    .attr('ry', DOT_R * 0.26)
    .attr('fill', 'rgba(255,255,255,0.38)')
    .attr('pointer-events', 'none');

  // ── Tag labels ──
  commits.each(function(d) {
    const tags = shaToTags.get(d.sha) ?? [];
    const el = d3.select(this);
    el.selectAll('g.tag-label').remove();
    tags.forEach((tagName, i) => {
      const w  = tagName.length * TAG_CHAR_W + TAG_PAD_X * 2;
      const ty = d.y - DOT_R - 4 - (TAG_H + 4) * (tags.length - i);
      const tg = el.append('g')
        .attr('class', 'tag-label')
        .style('cursor', 'pointer')
        .on('click', (event: MouseEvent) => {
          event.stopPropagation();
          onTagContextMenu(tagName, event);
        })
        .on('mouseenter', function() { d3.select(this).select('rect').attr('fill', 'var(--panel-2)'); })
        .on('mouseleave', function() { d3.select(this).select('rect').attr('fill', 'var(--panel)'); });
      tg.append('rect')
        .attr('x', d.x - w / 2)
        .attr('y', ty)
        .attr('width', w)
        .attr('height', TAG_H)
        .attr('rx', TAG_H / 2)
        .attr('fill', 'var(--panel)')
        .attr('stroke', TAG_COLOR)
        .attr('stroke-width', 1)
        .attr('pointer-events', 'visiblePainted');
      tg.append('text')
        .attr('x', d.x)
        .attr('y', ty + TAG_H / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('font-size', 10)
        .attr('font-family', 'monospace')
        .attr('fill', TAG_COLOR)
        .attr('pointer-events', 'none')
        .text(tagName);
    });
  });

  // Dashed stem — pas d'événements
  commits
    .selectAll<SVGLineElement, unknown>('line.stem')
    .data((d) => [d])
    .join('line')
    .attr('class', 'stem')
    .attr('x1', (d) => d.x)
    .attr('y1', (d) => d.bubbleAbove ? d.y - DOT_R : d.y + DOT_R)
    .attr('x2', (d) => d.x)
    .attr('y2', (d) => d.bubbleAbove ? d.y - DOT_R - d.bubbleDashH : d.y + DOT_R + DASH_H)
    .attr('stroke', 'var(--muted)')
    .attr('stroke-dasharray', '3,2')
    .attr('stroke-width', 1);

  // Range markers — flags
  commits.each(function(d) {
    const isStart = d.sha === rangeStartSha;
    const isEnd   = d.sha === rangeEndSha;
    const el = d3.select(this);
    el.selectAll('g.range-flag').remove();
    if (!isStart && !isEnd) return;

    const px = d.x;
    const poleBottom = d.y - DOT_R - 1;
    const poleTop    = poleBottom - 20;
    const fw = 14, fh = 10; // flag width × height

    const fg = el.append('g').attr('class', 'range-flag').attr('pointer-events', 'none');

    fg.append('line')
      .attr('x1', px).attr('y1', poleBottom)
      .attr('x2', px).attr('y2', poleTop)
      .attr('stroke', isStart ? RANGE_START_COLOR : 'var(--muted)')
      .attr('stroke-width', 1.5)
      .attr('stroke-linecap', 'round');

    if (isStart) {
      // Green pennant (triangle)
      fg.append('polygon')
        .attr('points', `${px},${poleTop} ${px + fw},${poleTop + fh / 2} ${px},${poleTop + fh}`)
        .attr('fill', RANGE_START_COLOR);
    } else {
      // Checkered flag — 4 cols × 2 rows
      const sqW = fw / 4, sqH = fh / 2;
      for (let row = 0; row < 2; row++) {
        for (let col = 0; col < 4; col++) {
          fg.append('rect')
            .attr('x', px + col * sqW).attr('y', poleTop + row * sqH)
            .attr('width', sqW).attr('height', sqH)
            .attr('fill', (row + col) % 2 === 0 ? '#111' : '#ddd');
        }
      }
    }
  });

  // Bubble rect + text (rebuild on every render via .each)
  commits.each(function (d) {
    const el = d3.select(this);
    const { lines, boxW, boxH } = computeBubble(d.subject);
    const bubbleY = d.bubbleAbove
      ? d.y - DOT_R - d.bubbleDashH - boxH
      : d.y + DOT_R + DASH_H;

    // Pas de .on('click', ...) ici contrairement à .dot ci-dessus : un clic sur la bulle épingle/
    // désépingle sa tooltip plutôt que d'ouvrir CommitContextMenu — voir handleClick (délégué sur
    // containerEl) dans l'effet Wheel zoom + drag-to-scroll.
    el.selectAll('rect.bubble').data([d]).join('rect')
      .attr('class', 'bubble')
      .attr('x', d.x - boxW / 2)
      .attr('y', bubbleY)
      .attr('width', boxW)
      .attr('height', boxH)
      .attr('rx', 4)
      .attr('fill', 'var(--panel-2)')
      .attr('stroke', 'var(--border)')
      .attr('stroke-width', 1)
      .attr('pointer-events', 'visiblePainted')
      .style('cursor', 'pointer');

    // Replace text element entirely so tspan children sync correctly
    el.selectAll('text.bubble-text').remove();
    const textEl = el.append('text')
      .attr('class', 'bubble-text')
      .attr('text-anchor', 'middle')
      .attr('font-size', 11)
      .attr('font-family', 'monospace')
      .attr('fill', 'var(--text)')
      .attr('pointer-events', 'none');

    lines.forEach((line, i) => {
      textEl.append('tspan')
        .attr('x', d.x)
        .attr('y', bubbleY + BUBBLE_PAD_Y / 2 + i * LINE_H + LINE_H / 2)
        .attr('dominant-baseline', 'central')
        .text(line);
    });
  });

  // ── Truncated history ghost nodes ──────────────────────────────────────────
  const GHOST_R = DOT_R - 1;

  g.selectAll<SVGLineElement, TruncatedRoot>('line.history-truncated-tail')
    .data(layout.truncatedRoots, (d) => d.sha)
    .join('line')
    .attr('class', 'history-truncated-tail')
    .attr('x1', (d) => d.childX - DOT_R)
    .attr('y1', (d) => d.childY)
    .attr('x2', (d) => d.x + GHOST_R)
    .attr('y2', (d) => d.y)
    .attr('stroke', (d) => d.color)
    .attr('stroke-width', 1.5)
    .attr('stroke-dasharray', '4,3')
    .attr('stroke-opacity', 0.4)
    .attr('pointer-events', 'none');

  const ghostGroups = g.selectAll<SVGGElement, TruncatedRoot>('g.history-truncated-root')
    .data(layout.truncatedRoots, (d) => d.sha)
    .join('g')
    .attr('class', 'history-truncated-root')
    .style('cursor', 'help');

  ghostGroups.selectAll<SVGTitleElement, TruncatedRoot>('title')
    .data((d) => [d])
    .join('title')
    .text('Historique tronqué — des commits plus anciens existent mais n\'ont pas été chargés');

  ghostGroups.selectAll<SVGCircleElement, TruncatedRoot>('circle')
    .data((d) => [d])
    .join('circle')
    .attr('cx', (d) => d.x)
    .attr('cy', (d) => d.y)
    .attr('r', GHOST_R)
    .attr('fill', (d) => d.color)
    .attr('fill-opacity', 0.15)
    .attr('stroke', (d) => d.color)
    .attr('stroke-width', 1.5)
    .attr('stroke-dasharray', '3,2')
    .attr('stroke-opacity', 0.5);

  ghostGroups.selectAll<SVGTextElement, TruncatedRoot>('text')
    .data((d) => [d])
    .join('text')
    .attr('x', (d) => d.x)
    .attr('y', (d) => d.y)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'central')
    .attr('font-size', 11)
    .attr('font-family', 'monospace')
    .attr('fill', (d) => d.color)
    .attr('fill-opacity', 0.6)
    .attr('pointer-events', 'none')
    .text('…');

  // ── Branch labels ──
  const labels = g
    .selectAll<SVGGElement, typeof layout.branchLabels[0]>('g.branch-label')
    .data(layout.branchLabels, (d) => d.name)
    .join('g')
    .attr('class', 'branch-label')
    .style('cursor', 'pointer') // inherited by the rect/text children below, same as edge/tip hitareas
    .call((sel) => sel.raise()); // always above edges and dots

  // Dashed connectors, on their own layer rather than inside g.branch-label: that group is raised
  // above everything just above, which made a connector cross OVER a tag pill (e.g. the label of
  // chore/GU-363 on the 2.2.0 tag) instead of passing behind it. Each line is re-inserted right
  // before the first commit group on every render — above the edges, but below the commit groups
  // (dots, tags, bubbles) — so a tag, which is opaque, hides the part of the link that goes
  // behind it. The datum is still the label, so the hover/click walk-up on '.label-connector'
  // (handleTooltipMove, contextmenu) keeps treating the line as part of its label.
  const firstCommitEl = g.select<SVGGElement>('g.commit').node();
  g.selectAll<SVGLineElement, LayoutBranchLabel>('line.label-connector')
    .data(layout.branchLabels.filter((d) => d.elbowX !== undefined), (d) => d.name)
    .join('line')
    .attr('class', 'label-connector')
    .attr('x1', (d) => d.x)
    .attr('y1', (d) => d.y + LABEL_H / 2)
    .attr('x2', (d) => d.elbowX!)
    .attr('y2', (d) => d.elbowY!)
    .attr('stroke', (d) => d.color)
    .attr('stroke-dasharray', '4,3')
    .attr('stroke-width', 1)
    .attr('stroke-opacity', 0.65)
    .each(function () { this.parentNode?.insertBefore(this, firstCommitEl); });

  labels
    .selectAll<SVGRectElement, unknown>('rect')
    .data((d) => [d])
    .join('rect')
    .attr('x', (d) => d.x - (d.name.length * LABEL_CHAR_W + LABEL_PAD_X * 2) / 2)
    .attr('y', (d) => d.y - LABEL_H / 2)
    .attr('width', (d) => d.name.length * LABEL_CHAR_W + LABEL_PAD_X * 2)
    .attr('height', LABEL_H)
    .attr('rx', 3)
    .attr('fill', 'var(--panel)')
    .attr('fill-opacity', 1)
    .attr('stroke', (d) => d.color)
    .attr('stroke-width', 1.5);

  labels
    .selectAll<SVGTextElement, unknown>('text')
    .data((d) => [d])
    .join('text')
    .attr('x', (d) => d.x)
    .attr('y', (d) => d.y)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'central')
    .attr('font-size', 10)
    .attr('font-family', 'monospace')
    .attr('fill', (d) => d.color)
    .text((d) => d.name);

  // ── Cherry-pick links ──
  const nodeMap = new Map(layout.nodes.map((n) => [n.sha, n]));
  type CpDatum = { id: string; src: LayoutNode; tgt: LayoutNode; path: string };
  const cpData = cherryPicks
    .map((cp) => {
      const src = nodeMap.get(cp.sourceSha);
      const tgt = nodeMap.get(cp.targetSha);
      return src && tgt ? { id: cp.id, src, tgt, path: cherryPickPath(src, tgt) } : null;
    })
    .filter((cp): cp is CpDatum => cp !== null);

  g.selectAll<SVGPathElement, CpDatum>('path.cherry-pick-link')
    .data(cpData, (d) => d.id)
    .join('path')
    .attr('class', 'cherry-pick-link')
    .attr('d', (d) => d.path)
    .attr('stroke', (d) => d.id === selectedCherryPickId ? '#ff9800' : d.src.color)
    .attr('stroke-width', (d) => d.id === selectedCherryPickId ? 4.5 : 3.5)
    .attr('stroke-dasharray', '6,3')
    .attr('stroke-opacity', (d) => !selectedCherryPickId || d.id === selectedCherryPickId ? 1 : 0.3)
    .attr('fill', 'none')
    .attr('marker-end', 'url(#graph-arrow)')
    .attr('pointer-events', 'none')
    // Driven by the parent's tooltip state (highlightedCherryPickId), not raw hover: the
    // hitarea's mousemove handler below only asks the parent to open/close its tooltip (which
    // has its own hide-delay + hover-into-tooltip cancellation, see RepoViewPage.tsx cpTooltip).
    // Setting the class here from that same state, instead of directly on hitarea enter/leave,
    // is what makes the highlight last exactly as long as the tooltip does — including while the
    // cursor is over the tooltip panel itself, off the link entirely.
    .classed('cp-hover', (d) => d.id === highlightedCherryPickId)
    .lower(); // below commits and labels

  // Invisible wide stroke on top of each cherry-pick link for easy hover detection — same
  // reasoning as path.edge-hitarea above: the visible dashed line (3.5-4.5px, sparse dashes) is
  // too thin/gappy to hover reliably. Commit dots, appended earlier and never re-lowered here,
  // keep capturing events first wherever they overlap this path.
  g.selectAll<SVGPathElement, CpDatum>('path.cherry-pick-hitarea')
    .data(cpData, (d) => d.id)
    .join('path')
    .attr('class', 'cherry-pick-hitarea')
    .attr('d', (d) => d.path)
    .attr('stroke', 'transparent')
    .attr('stroke-width', 10)
    .attr('fill', 'none')
    .attr('pointer-events', 'stroke')
    .style('cursor', 'pointer')
    .lower();

  // ── Open-branch tip arrows ──
  // For unmerged branches, draw a short horizontal extension with an arrowhead.
  // The trunk (main/master) is extended all the way to the rightmost non-trunk tip
  // so the graph has a clean, aligned right edge.
  const TIP_LEN = 36;
  const nonTrunkEndX = layout.openTips
    .filter((n) => n.branchName !== layout.trunkBranch)
    .reduce((max, n) => Math.max(max, n.x + DOT_R + TIP_LEN), 0);

  const tipEndX = (d: LayoutNode) => {
    const startX = d.x + DOT_R;
    const isTrunk = d.branchName === layout.trunkBranch;
    return isTrunk && nonTrunkEndX > startX ? nonTrunkEndX : startX + TIP_LEN;
  };

  // Le stroke-dasharray de l'animation "pipeline active" (voir path.edge plus haut) va
  // jusqu'à l'endpoint du path, sous le marker de flèche — mais le polygone du marker se
  // rétrécit en pointe, donc près de la pointe il ne recouvre plus toute la largeur du
  // stroke et laisse dépasser le motif animé. Pour les tips à pipeline active, on sépare
  // donc la ligne en deux : un segment animé (sans marker) qui s'arrête à la base de la
  // flèche (ARROW_MARKER_LEN, dérivé de la géométrie du marker — voir plus haut), et un
  // court "cap" statique qui porte le marker — la flèche reste un triangle plein, jamais
  // traversée par le motif animé.
  const [activeTips, quietTips] = [
    layout.openTips.filter((d) => activePipelineBranches.has(d.branchName)),
    layout.openTips.filter((d) => !activePipelineBranches.has(d.branchName)),
  ];

  g.selectAll<SVGPathElement, LayoutNode>('path.branch-tip')
    .data(quietTips, (d) => d.sha)
    .join('path')
    .attr('class', 'branch-tip')
    .attr('d', (d) => `M ${d.x + DOT_R} ${d.y} L ${tipEndX(d)} ${d.y}`)
    .attr('stroke', (d) => d.color)
    .attr('stroke-width', 5)
    .attr('fill', 'none')
    .attr('marker-end', 'url(#graph-arrow)')
    .lower();

  // Le cap déborde un peu (CAP_OVERLAP) sur le segment animé plutôt que de le toucher pile
  // au pixel près : `stroke-dasharray` peut afficher un "trou" (gap du motif) exactement à
  // la coupure selon la phase de l'animation à cet instant, ce qui décollerait visuellement
  // la base du triangle du bout de la ligne. Le cap doit donc être peint au-dessus du pulse
  // dans cette zone de recouvrement pour toujours le masquer, quelle que soit sa phase.
  const CAP_OVERLAP = 8;

  // .lower() place l'élément en premier enfant = peint en premier = en dessous. Le cap est
  // lowered ici, AVANT le pulse ci-dessous — le pulse (lowered en second) redevient premier
  // enfant à son tour, repoussant le cap juste après lui : le cap finit donc peint après
  // (au-dessus) le pulse dans leur zone de recouvrement.
  g.selectAll<SVGPathElement, LayoutNode>('path.branch-tip-cap')
    .data(activeTips, (d) => d.sha)
    .join('path')
    .attr('class', 'branch-tip-cap')
    .attr('d', (d) => `M ${tipEndX(d) - ARROW_MARKER_LEN - CAP_OVERLAP} ${d.y} L ${tipEndX(d)} ${d.y}`)
    .attr('stroke', (d) => d.color)
    .attr('stroke-width', 5)
    .attr('fill', 'none')
    .attr('marker-end', 'url(#graph-arrow)')
    .attr('pointer-events', 'none')
    .lower();

  g.selectAll<SVGPathElement, LayoutNode>('path.branch-tip-pulse')
    .data(activeTips, (d) => d.sha)
    .join('path')
    .attr('class', 'branch-tip-pulse edge-pipeline-active')
    .attr('d', (d) => `M ${d.x + DOT_R} ${d.y} L ${tipEndX(d) - ARROW_MARKER_LEN} ${d.y}`)
    .attr('stroke', (d) => d.color)
    .attr('stroke-width', 5)
    .attr('fill', 'none')
    .attr('pointer-events', 'none')
    .lower();

  // Invisible wide hit area on branch-tip lines for tooltip hover
  g.selectAll<SVGPathElement, LayoutNode>('path.branch-tip-hitarea')
    .data(layout.openTips, (d) => d.sha)
    .join('path')
    .attr('class', 'branch-tip-hitarea')
    .attr('d', (d) => {
      const startX = d.x + DOT_R;
      const isTrunk = d.branchName === layout.trunkBranch;
      const endX = isTrunk && nonTrunkEndX > startX ? nonTrunkEndX : startX + TIP_LEN;
      return `M ${startX} ${d.y} L ${endX} ${d.y}`;
    })
    .attr('stroke', 'transparent')
    .attr('stroke-width', 12)
    .attr('fill', 'none')
    .attr('pointer-events', 'stroke')
    .style('cursor', 'pointer');
}

// Même paire d'icônes que CommitContextMenu.tsx/SettingsModal.tsx — copier/copié, pour rester
// visuellement cohérent avec les autres boutons "copier le SHA complet" de l'app.
function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="13" height="13">
      <rect x="5" y="5" width="8" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3.5 10.5V3.5a1 1 0 0 1 1-1H10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function CopiedIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="13" height="13">
      <path d="M3.5 8.4 L6.5 11.4 L12.5 4.6" stroke="var(--success)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Punaise (tête + corps effilé + aiguille) plutôt qu'un marqueur de carte — se distingue
// épinglé/non-épinglé par la ROTATION (inclinée à 45°, comme posée du bout des doigts avant
// d'être enfoncée ↔ droite, enfoncée), jamais par la couleur : la forme seule porte l'état.
function PinIcon({ pinned }: { pinned?: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="13" height="13"
      style={{ transform: pinned ? 'rotate(0deg)' : 'rotate(45deg)' }}
    >
      <rect x="5.4" y="1.8" width="5.2" height="2" rx="0.9" fill="currentColor" />
      <path
        d="M6.2 3.8h3.6l-.5 4.3a1 1 0 0 1-.15.42L8 10.6l-1.15-2.08a1 1 0 0 1-.15-.42L6.2 3.8Z"
        stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"
      />
      <line x1="8" y1="10.6" x2="8" y2="14.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function ZoomInIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="15" height="15">
      <path d="M8 4.5v7M4.5 8h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ZoomOutIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="15" height="15">
      <path d="M4.5 8h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function FitToViewIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="15" height="15">
      <path
        d="M2 5.5V3a1 1 0 0 1 1-1h2.5M14 5.5V3a1 1 0 0 0-1-1h-2.5M2 10.5V13a1 1 0 0 0 1 1h2.5M14 10.5V13a1 1 0 0 1-1 1h-2.5"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

// Défaut stable pour `branchColorRules` : un littéral `= []` dans la signature crée un nouveau tableau
// à chaque rendu quand la prop est absente (métadonnées du repo pas chargées ou en erreur), ce qui
// invalide le useMemo du layout, relance l'effet `onVisibleShas` (nouveau Set → setState du parent) et
// fait boucler le rendu en continu.
const NO_BRANCH_COLOR_RULES: BranchColorRule[] = [];

export function GitGraph({ repoId, graph, selectedBranches, rangeStartSha, rangeEndSha, onCommitContextMenu, onTagContextMenu, selectedCherryPickId, focusedCommitSha, focusedBranchName, onVisibleShas, onCherryPickHover, highlightedCherryPickId, onBranchContextMenu, onCommitHover, onConfigureCi, initialView, onViewChange, theme, branchColorRules = NO_BRANCH_COLOR_RULES, jiraBaseUrl }: Props) {
  const model  = useMemo(() => buildGitgraphModel(graph, selectedBranches), [graph, selectedBranches]);
  const layout = useMemo(
    () => computeLayout(model, graph.defaultBranch, graph.cherryPicks, branchColorRules),
    [model, graph.defaultBranch, graph.cherryPicks, branchColorRules],
  );
  const shaToTags = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const [name, sha] of Object.entries(model.tags)) {
      const list = map.get(sha) ?? [];
      list.push(name);
      map.set(sha, list);
    }
    return map;
  }, [model.tags]);

  const summarizedQuery = useQuery({
    queryKey: ['summarized-shas', repoId],
    queryFn: () => api.summarizedShas(repoId),
    staleTime: 30_000,
  });
  const summarizedShas = useMemo(
    () => new Set(summarizedQuery.data?.shas ?? []),
    [summarizedQuery.data],
  );

  // GU-380 : un seul appel par dépôt (index des packages, caché côté API). Un échec (registre désactivé,
  // 403) laisse simplement le graphe sans marqueurs ; pas de retry.
  const packageCommitsQuery = useQuery({
    queryKey: ['package-commits', repoId],
    queryFn: () => api.packageCommits(repoId),
    staleTime: 60_000,
    retry: false,
  });
  const packageShas = useMemo(
    () => new Set(packageCommitsQuery.data?.shas ?? []),
    [packageCommitsQuery.data],
  );
  const packagesTruncated = packageCommitsQuery.data?.truncated ?? false;

  const svgRef          = useRef<SVGSVGElement>(null);
  const gRef            = useRef<SVGGElement>(null);
  const containerRef    = useRef<HTMLDivElement>(null);
  const minimapCanvasRef = useRef<HTMLCanvasElement>(null);
  const scaleRef        = useRef(1);
  const initialFitDone  = useRef(false);
  // Ponts vers les fonctions de zoom définies dans l'effet wheel/drag ci-dessous (deps []) — même
  // raison que drawMinimapRef/branchHoverRef : les boutons +/−/ajuster et le raccourci clavier
  // vivent dans le JSX / un effet séparé, sans accès direct aux closures de cet effet-là.
  const zoomControlsRef = useRef<{ zoomIn: () => void; zoomOut: () => void; fitToView: () => void } | null>(null);
  const layoutRef       = useRef(layout);
  const cherryPicksRef  = useRef(graph.cherryPicks);
  const shaToTagsRef    = useRef(shaToTags);
  const drawMinimapRef  = useRef<(() => void) | null>(null);
  // setBranchHover est défini à l'intérieur de l'effet "Wheel zoom + drag-to-scroll" (deps []),
  // pour la même raison que drawMinimapRef ci-dessus : l'effet "fly to branch" plus bas (son
  // propre effet, deps [focusedBranchName]) doit pouvoir déclencher le même highlight que le
  // survol d'une branche dans le graphe, sans dupliquer sa logique (classes .branch-hover sur
  // edges/dots/tips/label) ni la sortir de cet effet-ci, qui la referme déjà sur gEl/minimapHoverRef.
  const branchHoverRef = useRef<((branchName: string | null) => void) | null>(null);
  // Lu par drawMinimap (deps []) pour reproduire le branch-hover/dot-hover du graphe principal
  // sur le minimap canvas — pas de state React ici, drawMinimap est redéclenché à la main via
  // drawMinimapRef.current?.() à chaque changement, comme pour le scroll/zoom.
  const minimapHoverRef = useRef<{ branchName: string | null; dotSha: string | null }>({ branchName: null, dotSha: null });
  // Même raison que layoutRef/cherryPicksRef ci-dessus : drawMinimap (deps []) ne peut pas fermer
  // sur `theme` directement, et le canvas ne peut pas suivre var(--...) tout seul comme le SVG.
  const themeRef = useRef(theme);
  const hideTimeoutRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tooltip, setTooltip]             = useState<TooltipState>(null);
  const [tooltipCopied, setTooltipCopied] = useState(false);
  // Même raison que layoutRef/cherryPicksRef : lu depuis handleTooltipMove/handleClick/
  // onContainerLeave, définis dans l'effet Wheel zoom + drag-to-scroll (deps []) — `tooltip` state
  // y serait figé au premier rendu, ce ref reste à jour.
  const tooltipStateRef = useRef<TooltipState>(tooltip);
  useEffect(() => { tooltipStateRef.current = tooltip; }, [tooltip]);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { left: tooltipLeft, top: tooltipTop } = useClampedMenuPosition(
    tooltipRef,
    (tooltip?.x ?? 0) + 14,
    (tooltip?.y ?? 0) + 14,
  );
  const branchInfoRef = useRef<Map<string, { isDefault: boolean; isProtected: boolean; isMerged: boolean; originSha: string }>>(new Map());
  // Le menu de branche est maintenant ouvert au clic (BranchContextMenu, géré par le parent) —
  // ce ref garde la dernière valeur du callback pour handleClick ci-dessous, qui vit dans un
  // effet à deps [] (voir Wheel zoom + drag-to-scroll) et ne doit donc pas fermer sur une
  // référence figée au premier rendu.
  const onBranchContextMenuRef = useRef(onBranchContextMenu);
  useEffect(() => { onBranchContextMenuRef.current = onBranchContextMenu; }, [onBranchContextMenu]);

  // Même raison : lu depuis le handler onScroll de l'effet à deps [] plus bas.
  const onViewChangeRef = useRef(onViewChange);
  useEffect(() => { onViewChangeRef.current = onViewChange; }, [onViewChange]);

  // Même raison : lu depuis setCpHover, défini dans l'effet à deps [] plus bas (survol des liens
  // cherry-pick).
  const onCherryPickHoverRef = useRef(onCherryPickHover);
  useEffect(() => { onCherryPickHoverRef.current = onCherryPickHover; }, [onCherryPickHover]);

  // Même besoin dans l'autre sens : la tooltip de commit (message au survol d'un dot) doit aussi
  // fermer le menu contextuel ouvert par un clic précédent sur un dot différent.
  useEffect(() => {
    if (tooltip) onCommitHover?.();
  }, [tooltip, onCommitHover]);

  // Keep layoutRef current so the zoom handler always has the latest dimensions
  useEffect(() => { layoutRef.current = layout; }, [layout]);
  useEffect(() => { cherryPicksRef.current = graph.cherryPicks; }, [graph.cherryPicks]);
  useEffect(() => { shaToTagsRef.current = shaToTags; }, [shaToTags]);

  // Notify parent of the SHAs currently rendered in the graph
  useEffect(() => {
    onVisibleShas?.(new Set(layout.nodes.map((n) => n.sha)));
  }, [layout, onVisibleShas]);

  // Build branch metadata lookup (default/protected/merged flags + origin SHA)
  const branchInfo = useMemo(() => {
    const map = new Map<string, { isDefault: boolean; isProtected: boolean; isMerged: boolean; originSha: string }>();
    const commitParents = new Map(graph.commits.map((c) => [c.sha, c.parents]));
    const openTipBranches = new Set(layout.openTips.map((n) => n.branchName));
    const branchOldest = new Map<string, LayoutNode>();
    for (const node of layout.nodes) {
      const cur = branchOldest.get(node.branchName);
      if (!cur || node.x < cur.x) branchOldest.set(node.branchName, node);
    }
    const branchDtoMap = new Map(graph.branches.map((b) => [b.name, b]));
    for (const [branchName, oldestNode] of branchOldest) {
      const parents = commitParents.get(oldestNode.sha) ?? [];
      const dto = branchDtoMap.get(branchName);
      map.set(branchName, {
        isDefault: dto?.isDefault ?? false,
        isProtected: dto?.isProtected ?? false,
        isMerged: !openTipBranches.has(branchName),
        originSha: parents[0] ?? '',
      });
    }
    return map;
  }, [graph.branches, graph.commits, layout.nodes, layout.openTips]);
  useEffect(() => { branchInfoRef.current = branchInfo; }, [branchInfo]);

  // Contrairement au pipelineQuery de BranchContextMenu.tsx (une branche à la fois, au clic),
  // celle-ci tourne en continu pour surligner toutes les branches actives dans le graphe — d'où
  // le polling fixe plutôt que conditionnel : on ne sait pas encore quelles branches sont
  // actives avant la première réponse.
  const activePipelinesQuery = useQuery({
    queryKey: ['active-pipelines', repoId],
    queryFn: () => api.activePipelines(repoId),
    staleTime: 10_000,
    refetchInterval: 20_000,
  });
  const activePipelineBranches = useMemo(
    () => new Set((activePipelinesQuery.data ?? []).map((p) => p.branchName)),
    [activePipelinesQuery.data],
  );

  const drawMinimap = useCallback(() => {
    const canvas = minimapCanvasRef.current;
    const container = containerRef.current;
    const lay = layoutRef.current;
    if (!canvas || !container || lay.svgWidth === 0 || lay.svgHeight === 0) return;

    const W = canvas.offsetWidth || 200;
    const H = 76;
    if (canvas.width !== W) canvas.width = W;
    canvas.height = H;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const sx = W / lay.svgWidth;
    const sy = H / lay.svgHeight;

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = cssVar('--graph-minimap-bg');
    ctx.fillRect(0, 0, W, H);

    drawMinimapLaneLines(ctx, lay.nodes, sy, W);

    const { branchName: hoveredBranchName, dotSha: hoveredDotSha } = minimapHoverRef.current;

    drawMinimapEdges(ctx, lay.edges, sx, sy);
    if (hoveredBranchName) drawMinimapHoveredBranch(ctx, lay.edges, hoveredBranchName, sx, sy);

    const nodeMap = new Map(lay.nodes.map((n) => [n.sha, n]));
    drawMinimapCherryPicks(ctx, cherryPicksRef.current, nodeMap, sx, sy);

    drawMinimapDots(ctx, lay.nodes, shaToTagsRef.current, hoveredBranchName, hoveredDotSha, sx, sy);

    const k = scaleRef.current;
    const viewportOverlay = MINIMAP_VIEWPORT_OVERLAY[themeRef.current];
    drawMinimapViewportRect(ctx, container, k, sx, sy, W, H, viewportOverlay);
  }, []);

  useEffect(() => { drawMinimapRef.current = drawMinimap; }, [drawMinimap]);
  // Le canvas n'a aucun moyen de suivre un changement de thème tout seul (contrairement au SVG,
  // qui lit var(--...) à chaque repaint) — sans ce redraw explicite, basculer dark/light dans les
  // Paramètres laisserait la minimap figée dans l'ancien thème jusqu'au prochain scroll/zoom.
  useEffect(() => {
    themeRef.current = theme;
    drawMinimap();
  }, [theme, drawMinimap]);

  const handleMinimapInteract = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = minimapCanvasRef.current;
    const container = containerRef.current;
    const lay = layoutRef.current;
    if (!canvas || !container) return;
    const rect = canvas.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / canvas.width)  * lay.svgWidth;
    const svgY = ((e.clientY - rect.top)  / canvas.height) * lay.svgHeight;
    const k = scaleRef.current;
    container.scrollLeft = svgX * k - container.clientWidth  / 2;
    container.scrollTop  = svgY * k - container.clientHeight / 2;
    drawMinimap();
  }, [drawMinimap]);

  // Wheel zoom + drag-to-scroll — set up once on mount
  useEffect(() => {
    const svgEl       = svgRef.current;
    const gEl         = gRef.current;
    const containerEl = containerRef.current;
    if (!svgEl || !gEl || !containerEl) return;

    const applySVGScale = (k: number) => {
      scaleRef.current = k;
      const { svgWidth, svgHeight } = layoutRef.current;
      const w = Math.round(svgWidth  * k);
      const h = Math.round(svgHeight * k);
      // Set both attribute AND inline style so no CSS rule can constrain the dimensions
      svgEl.setAttribute('width',  String(w));
      svgEl.setAttribute('height', String(h));
      svgEl.style.width  = `${w}px`;
      svgEl.style.height = `${h}px`;
      gEl.setAttribute('transform', `scale(${k})`);
      drawMinimapRef.current?.();
    };

    // Normalize delta: wheel events can be pixels (trackpad) or lines (mouse wheel)
    const toPx = (delta: number, mode: number) => mode === 1 ? delta * 40 : delta;

    // Zoom to newKRaw (clamped), keeping the point under (anchorClientX, anchorClientY) fixed on
    // screen — the viewport's own center when no anchor is given (button/keyboard zoom has no
    // cursor position to anchor on, unlike wheel zoom below). Shared by handleWheel's ctrl branch
    // and the zoomIn/zoomOut controls exposed via zoomControlsRef, so both stay in sync instead of
    // two copies of the same anchoring math drifting apart.
    const zoomTo = (newKRaw: number, anchorClientX?: number, anchorClientY?: number) => {
      const rect = containerEl.getBoundingClientRect();
      const cx = anchorClientX ?? rect.left + containerEl.clientWidth / 2;
      const cy = anchorClientY ?? rect.top + containerEl.clientHeight / 2;
      const svgX = (containerEl.scrollLeft + cx - rect.left) / scaleRef.current;
      const svgY = (containerEl.scrollTop  + cy - rect.top)  / scaleRef.current;
      const newK = Math.max(0.1, Math.min(4, newKRaw));
      applySVGScale(newK);
      containerEl.scrollLeft = svgX * newK - (cx - rect.left);
      containerEl.scrollTop  = svgY * newK - (cy - rect.top);
    };

    // Même formule que le fit-to-view du premier chargement plus bas (isFirstFit) — zoom arrière
    // uniquement (Math.min(1, ...)) : un petit graphe ne doit pas être artificiellement agrandi
    // au-delà de sa taille réelle.
    const fitToView = () => {
      const { svgWidth, svgHeight } = layoutRef.current;
      if (containerEl.clientWidth <= 0 || containerEl.clientHeight <= 0 || svgWidth <= 0 || svgHeight <= 0) return;
      const k = Math.min(1, containerEl.clientWidth / svgWidth, containerEl.clientHeight / svgHeight);
      applySVGScale(k);
      containerEl.scrollLeft = 0;
      containerEl.scrollTop = 0;
    };

    zoomControlsRef.current = {
      zoomIn: () => zoomTo(scaleRef.current * 1.15),
      zoomOut: () => zoomTo(scaleRef.current / 1.15),
      fitToView,
    };

    const handleWheel = (e: WheelEvent) => {
      if (!containerEl.contains(e.target as Node)) return;
      e.preventDefault();
      if (e.ctrlKey) {
        // Ctrl + wheel → zoom anchored on cursor
        const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        zoomTo(scaleRef.current * factor, e.clientX, e.clientY);
      } else {
        // shiftKey+deltaY = horizontal wheel on some Logitech mice (Linux/Windows)
        const isHorizWheel = e.shiftKey && e.deltaX === 0;
        containerEl.scrollLeft += toPx(isHorizWheel ? e.deltaY : e.deltaX, e.deltaMode);
        containerEl.scrollTop  += toPx(isHorizWheel ? 0        : e.deltaY, e.deltaMode);
      }
    };

    // Survol d'une branche (edge-hitarea/branch-tip-hitarea) → mise en valeur en manipulant
    // directement les attributs/classes D3 sur les éléments déjà présents dans le DOM, sans
    // passer par le state React ni par renderGraph : renderGraph refait un plein rebuild (bulles,
    // labels, tags via .each()) à chaque appel, ce qui serait trop coûteux à déclencher sur
    // chaque mousemove. La classe CSS l'emporte sur les attributs de présentation posés par D3
    // (stroke-width, etc.) quelle que soit leur valeur — pas besoin de mémoriser/restaurer une
    // valeur d'origine, il suffit de retirer la classe.
    let hoveredBranch: string | null = null;
    const setBranchHover = (branchName: string | null) => {
      if (hoveredBranch === branchName) return;
      hoveredBranch = branchName;
      minimapHoverRef.current = { ...minimapHoverRef.current, branchName };
      drawMinimapRef.current?.();
      const g = d3.select(gEl);
      const isHovered = (d: { branchName: string }) => d.branchName === branchName;
      g.selectAll<SVGPathElement, LayoutEdge>('path.edge').classed('branch-hover', isHovered);
      g.selectAll<SVGGElement, LayoutNode>('g.commit').classed('branch-hover', isHovered);
      g.selectAll<SVGPathElement, LayoutNode>('path.branch-tip, path.branch-tip-cap, path.branch-tip-pulse')
        .classed('branch-hover', isHovered);
      g.selectAll<SVGGElement, { name: string }>('g.branch-label')
        .classed('branch-hover', (d) => d.name === branchName);
    };
    branchHoverRef.current = setBranchHover;

    // Survol d'un dot → seul ce dot est mis en valeur (même style visuel que .branch-hover sur
    // un dot, voir styles.css), pas toute sa branche — cas distinct de setBranchHover ci-dessus.
    let hoveredDotSha: string | null = null;
    const setDotHover = (sha: string | null) => {
      if (hoveredDotSha === sha) return;
      hoveredDotSha = sha;
      minimapHoverRef.current = { ...minimapHoverRef.current, dotSha: sha };
      drawMinimapRef.current?.();
      d3.select(gEl)
        .selectAll<SVGCircleElement, LayoutNode>('circle.dot')
        .classed('dot-hover', (d) => d.sha === sha);
    };

    // Survol d'un lien cherry-pick : contrairement à setBranchHover/setDotHover ci-dessus, ne
    // pose PAS la classe de surbrillance ici — seulement notifier le parent, qui décide d'ouvrir
    // sa tooltip et la garde affichée selon sa propre logique de délai/annulation. La classe
    // '.cp-hover' est posée par renderGraph à partir de highlightedCherryPickId (prop dérivée de
    // cet état côté parent), pour que le surlignage dure exactement aussi longtemps que la
    // tooltip — y compris quand le curseur est sur la tooltip elle-même, plus sur le lien. La
    // position (x, y) n'est transmise qu'à l'entrée dans un nouveau lien — comme la tooltip de
    // bulle de commit plus bas ("Fix position on first entry only") — pas à chaque mousemove tant
    // qu'on reste sur le même lien, pour ne pas faire suivre le curseur à la tooltip du parent.
    let hoveredCpId: string | null = null;
    const setCpHover = (id: string | null, x?: number, y?: number) => {
      if (hoveredCpId === id) return;
      hoveredCpId = id;
      onCherryPickHoverRef.current?.(id ? { id, x: x!, y: y! } : null);
    };

    let dragging = false;
    let drag = { x: 0, y: 0, sl: 0, st: 0 };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      dragging  = true;
      drag      = { x: e.clientX, y: e.clientY, sl: containerEl.scrollLeft, st: containerEl.scrollTop };
      containerEl.classList.add('is-dragging');
      e.preventDefault();
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      containerEl.scrollLeft = drag.sl - (e.clientX - drag.x);
      containerEl.scrollTop  = drag.st - (e.clientY - drag.y);
    };
    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      containerEl.classList.remove('is-dragging');
    };

    // Tooltip de commit uniquement désormais — la tooltip de branche est devenue un menu au
    // clic (voir handleClick ci-dessous), plus au survol.
    const handleTooltipMove = (e: MouseEvent) => {
      // Épinglée (clic sur une bulle, voir handleClick) : ignore tout survol ailleurs dans le
      // graphe tant qu'elle n'est pas explicitement refermée — sinon un simple passage de la
      // souris sur une autre bulle en chemin vers la tooltip la remplacerait ou la fermerait.
      if (tooltipStateRef.current?.pinned) return;
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      const target = e.target as Element;

      if (target?.classList.contains('bubble')) {
        const datum = (target as any).__data__ as LayoutNode;
        if (datum) {
          setBranchHover(null);
          setDotHover(null);
          setCpHover(null);
          // Fix position on first entry only — don't follow the cursor within the same bubble
          setTooltip((prev) =>
            prev?.sha === datum.sha
              ? prev
              : { text: datum.body, sha: datum.sha, authorName: datum.authorName, authoredAt: datum.authoredAt, x: e.clientX, y: e.clientY, pinned: false },
          );
          return;
        }
      }

      if (target?.classList.contains('cherry-pick-hitarea')) {
        const datum = (target as any).__data__ as { id: string };
        setBranchHover(null);
        setDotHover(null);
        setCpHover(datum?.id ?? null, e.clientX, e.clientY);
      } else if (target?.classList.contains('edge-hitarea') || target?.classList.contains('branch-tip-hitarea')) {
        const datum = (target as any).__data__ as LayoutEdge | LayoutNode;
        setBranchHover(datum?.branchName ?? null);
        setDotHover(null);
        setCpHover(null);
      } else if (target?.closest('.branch-label, .label-connector')) {
        // rect/text are plain children of g.branch-label and the connector is a sibling layer
        // (see "Dashed connectors" above), none with its own hitarea — walk up to the nearest
        // element carrying the label datum, same __data__ trick as the other branches here.
        const datum = (target.closest('.branch-label, .label-connector') as any)?.__data__ as { name: string } | undefined;
        setBranchHover(datum?.name ?? null);
        setDotHover(null);
        setCpHover(null);
      } else if (target?.classList.contains('dot')) {
        const datum = (target as any).__data__ as LayoutNode;
        setBranchHover(null);
        setDotHover(datum?.sha ?? null);
        setCpHover(null);
      } else {
        setBranchHover(null);
        setDotHover(null);
        setCpHover(null);
      }

      hideTimeoutRef.current = setTimeout(() => setTooltip(null), 120);
    };

    // Ouvre BranchContextMenu (géré par le parent) au clic sur une arête ou un tip de branche —
    // même mécanisme que .dot pour CommitContextMenu (voir renderGraph plus bas), mais en
    // délégation sur le conteneur plutôt qu'un .on('click', ...) D3 direct, pour réutiliser la
    // même classification de cible que handleTooltipMove sans toucher à renderGraph.
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;

      // Épingle/désépingle la tooltip de commit — .dot garde son propre .on('click', ...) dans
      // renderGraph pour ouvrir CommitContextMenu ; .bubble n'en a plus (voir renderGraph), son
      // clic passe uniquement par ici pour ne pas ouvrir les deux à la fois sur le même geste.
      if (target?.classList.contains('bubble')) {
        const datum = (target as any).__data__ as LayoutNode;
        if (!datum) return;
        setTooltip((prev) =>
          prev?.pinned && prev.sha === datum.sha
            ? null
            : { text: datum.body, sha: datum.sha, authorName: datum.authorName, authoredAt: datum.authoredAt, x: e.clientX, y: e.clientY, pinned: true },
        );
        return;
      }
      // N'importe quel autre clic dans le graphe (canvas vide, dot, edge, branche…) referme une
      // tooltip épinglée — pas de retour ici, le clic peut aussi ouvrir un menu contextuel juste
      // en dessous (les deux ne sont pas exclusifs : fermer l'ancienne tooltip puis ouvrir le menu
      // de branche cliqué, par exemple).
      if (tooltipStateRef.current?.pinned) setTooltip(null);

      const labelGroup = target?.closest('.branch-label, .label-connector');
      if (labelGroup) {
        // Same __data__ walk-up as handleTooltipMove — rect/text are plain children, not their
        // own hitarea. LayoutBranchLabel already carries { name, color }, same shape as the
        // edge/tip datum below, so the rest of the branch-context-menu wiring is unchanged.
        const datum = (labelGroup as any).__data__ as { name: string; color: string } | undefined;
        if (!datum?.name || !datum.color) return;
        const info = branchInfoRef.current.get(datum.name);
        onBranchContextMenuRef.current({ name: datum.name, color: datum.color, ...info }, e);
        return;
      }
      if (!(target?.classList.contains('edge-hitarea') || target?.classList.contains('branch-tip-hitarea'))) return;
      const datum = (target as any).__data__ as LayoutEdge | LayoutNode;
      const branchName = datum?.branchName;
      const color = datum?.color;
      if (!branchName || !color) return;
      const info = branchInfoRef.current.get(branchName);
      onBranchContextMenuRef.current({ name: branchName, color, ...info }, e);
    };

    // Tout changement de position ET de zoom passe par une assignation scrollLeft/scrollTop à un
    // moment ou un autre (wheel-pan, drag-to-scroll, ctrl+wheel zoom qui recentre, navigation
    // minimap, smooth-scroll vers un commit/cherry-pick) — un seul point d'accroche suffit donc
    // pour persister les deux. Debounce (pas de PATCH par pixel de scroll) : le timer redémarre à
    // chaque event, ne se déclenche vraiment qu'une fois l'utilisateur arrêté.
    let persistTimeout: ReturnType<typeof setTimeout> | null = null;
    const onScroll = () => {
      drawMinimapRef.current?.();
      if (persistTimeout) clearTimeout(persistTimeout);
      persistTimeout = setTimeout(() => {
        onViewChangeRef.current?.({
          scrollLeft: containerEl.scrollLeft,
          scrollTop: containerEl.scrollTop,
          scale: scaleRef.current,
        });
      }, 800);
    };
    // mousemove ne se déclenche plus une fois le curseur sorti du conteneur — sans ce handler,
    // une branche survolée juste avant de quitter le graphe resterait visuellement figée en
    // surbrillance. Même raison pour la tooltip de commit (`tooltip`) : si le curseur quitte le
    // conteneur d'un mouvement assez rapide pour ne générer aucun mousemove intermédiaire dessus
    // (handleTooltipMove ne se déclenche alors jamais, son délai de fermeture de 120ms n'est donc
    // jamais programmé), elle restait sinon affichée indéfiniment. Épinglée : ne pas la fermer ici
    // — c'est justement pour pouvoir sortir du conteneur (la tooltip elle-même est un élément
    // frère, hors de `containerEl`) et aller cliquer un bouton dedans sans qu'elle se referme.
    const onContainerLeave = () => {
      setBranchHover(null);
      setDotHover(null);
      setCpHover(null);
      if (tooltipStateRef.current?.pinned) return;
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      setTooltip(null);
    };

    // +/−/0 sans modificateur (pas Ctrl+/Ctrl- qui zoomeraient la page du navigateur à la place) —
    // pensé pour les utilisateurs sans souris (trackpad seul), en complément des boutons de
    // zoom/ajuster affichés dans le coin du graphe. Ignoré pendant la saisie dans un champ, pour
    // ne pas intercepter un "+"/"-"/"0" tapé normalement ailleurs sur la page (recherche de commit,
    // note de cherry-pick…).
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomControlsRef.current?.zoomIn(); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomControlsRef.current?.zoomOut(); }
      else if (e.key === '0') { e.preventDefault(); zoomControlsRef.current?.fitToView(); }
    };

    window.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    window.addEventListener('keydown', handleKeyDown);
    containerEl.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    containerEl.addEventListener('mousemove', handleTooltipMove);
    containerEl.addEventListener('mouseleave', onContainerLeave);
    containerEl.addEventListener('click', handleClick);
    containerEl.addEventListener('scroll', onScroll);

    return () => {
      if (persistTimeout) clearTimeout(persistTimeout);
      window.removeEventListener('wheel', handleWheel, { capture: true });
      window.removeEventListener('keydown', handleKeyDown);
      containerEl.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
      containerEl.removeEventListener('mousemove', handleTooltipMove);
      containerEl.removeEventListener('mouseleave', onContainerLeave);
      containerEl.removeEventListener('click', handleClick);
      containerEl.removeEventListener('scroll', onScroll);
      zoomControlsRef.current = null;
    };
  }, []);

  // D3 data render — runs whenever layout changes
  useEffect(() => {
    if (!gRef.current || !svgRef.current) return;
    const rangeEdgeIds = computeRangeEdgeIds(graph.commits, rangeStartSha, rangeEndSha);
    renderGraph(d3.select(gRef.current), layout, onCommitContextMenu, onTagContextMenu, rangeStartSha, rangeEndSha, rangeEdgeIds, graph.cherryPicks, shaToTags, selectedCherryPickId, summarizedShas, activePipelineBranches, highlightedCherryPickId, packageShas);
    // On first load, restore the saved position/zoom (initialView) if there is one, else fit the
    // whole graph within the viewport (zoom out only). initialView === undefined means the parent
    // hasn't resolved the preference yet — wait rather than fit-then-correct (visible flash), it
    // resolves quickly in practice (préchauffé dès App.tsx, voir la conversation).
    let isFirstFit = false;
    if (!initialFitDone.current && containerRef.current && initialView !== undefined) {
      const el = containerRef.current;
      if (el.clientWidth > 0 && el.clientHeight > 0 && layout.svgWidth > 0 && layout.svgHeight > 0) {
        scaleRef.current = initialView
          ? initialView.scale
          : Math.min(1, el.clientWidth / layout.svgWidth, el.clientHeight / layout.svgHeight);
        initialFitDone.current = true;
        isFirstFit = true;
      }
    }
    // Sync SVG dimensions with current scale after every layout change
    const k = scaleRef.current;
    const w = Math.round(layout.svgWidth  * k);
    const h = Math.round(layout.svgHeight * k);
    svgRef.current.setAttribute('width',  String(w));
    svgRef.current.setAttribute('height', String(h));
    svgRef.current.style.width  = `${w}px`;
    svgRef.current.style.height = `${h}px`;
    gRef.current.setAttribute('transform', `scale(${k})`);
    // Le scroll ne peut être restauré qu'une fois le SVG redimensionné ci-dessus — assigné trop
    // tôt, le navigateur le clampe à l'ancienne étendue scrollable et la valeur voulue est perdue.
    if (isFirstFit && initialView && containerRef.current) {
      containerRef.current.scrollLeft = initialView.scrollLeft;
      containerRef.current.scrollTop = initialView.scrollTop;
    }
    drawMinimap();
  }, [layout, onCommitContextMenu, onTagContextMenu, rangeStartSha, rangeEndSha, graph.commits, graph.cherryPicks, shaToTags, selectedCherryPickId, summarizedShas, activePipelineBranches, highlightedCherryPickId, packageShas, drawMinimap, initialView]);

  // Zoom + smooth-scroll to frame the selected cherry-pick when selection changes
  useEffect(() => {
    if (!selectedCherryPickId || !containerRef.current || !svgRef.current || !gRef.current) return;
    const cp = graph.cherryPicks.find((c) => c.id === selectedCherryPickId);
    if (!cp) return;
    const nodeMap = new Map(layoutRef.current.nodes.map((n) => [n.sha, n]));
    const n1 = nodeMap.get(cp.sourceSha);
    const n2 = nodeMap.get(cp.targetSha);
    if (!n1 || !n2) return;

    const el = containerRef.current;
    const PADDING = 140; // SVG-space px around the pair
    const spanX = Math.abs(n2.x - n1.x) + PADDING * 2;
    const spanY = Math.abs(n2.y - n1.y) + PADDING * 2;
    const newK = Math.max(0.3, Math.min(2, Math.min(el.clientWidth / spanX, el.clientHeight / spanY)));

    // Apply scale
    scaleRef.current = newK;
    const { svgWidth, svgHeight } = layoutRef.current;
    const w = Math.round(svgWidth  * newK);
    const h = Math.round(svgHeight * newK);
    svgRef.current.setAttribute('width',  String(w));
    svgRef.current.setAttribute('height', String(h));
    svgRef.current.style.width  = `${w}px`;
    svgRef.current.style.height = `${h}px`;
    gRef.current.setAttribute('transform', `scale(${newK})`);

    // Center scroll on midpoint
    el.scrollTo({
      left: ((n1.x + n2.x) / 2) * newK - el.clientWidth  / 2,
      top:  ((n1.y + n2.y) / 2) * newK - el.clientHeight / 2,
      behavior: 'smooth',
    });
  }, [selectedCherryPickId, graph.cherryPicks]);

  // Zoom to 1× scale, scroll to commit, then highlight its bubble for 3s
  useEffect(() => {
    if (!focusedCommitSha || !containerRef.current || !svgRef.current || !gRef.current) return;
    const node = layoutRef.current.nodes.find((n) => n.sha === focusedCommitSha);
    if (!node) return;

    const el = containerRef.current;
    const TARGET_K = 1.0;
    scaleRef.current = TARGET_K;
    const { svgWidth, svgHeight } = layoutRef.current;
    const w = Math.round(svgWidth  * TARGET_K);
    const h = Math.round(svgHeight * TARGET_K);
    svgRef.current.setAttribute('width',  String(w));
    svgRef.current.setAttribute('height', String(h));
    svgRef.current.style.width  = `${w}px`;
    svgRef.current.style.height = `${h}px`;
    gRef.current.setAttribute('transform', `scale(${TARGET_K})`);

    el.scrollTo({
      left: node.x * TARGET_K - el.clientWidth  / 2,
      top:  node.y * TARGET_K - el.clientHeight / 2,
      behavior: 'smooth',
    });

    // Highlight the bubble rect then fade back after 3s
    const bubbleRect = d3.select(gRef.current)
      .selectAll<SVGGElement, LayoutNode>('g.commit')
      .filter((d) => d.sha === focusedCommitSha)
      .select<SVGRectElement>('rect.bubble');

    if (bubbleRect.empty()) return;

    bubbleRect
      .attr('fill', cssVar('--bubble-highlight-bg'))
      .attr('stroke', cssVar('--info'))
      .attr('stroke-width', 2.5);

    const timer = setTimeout(() => {
      bubbleRect
        .transition()
        .duration(800)
        .attr('fill', cssVar('--panel-2'))
        .attr('stroke', cssVar('--border'))
        .attr('stroke-width', 1);
    }, 3000);

    return () => clearTimeout(timer);
  }, [focusedCommitSha]);

  // Zoom to 1× scale, scroll to a branch's label, then highlight the whole branch for 3s —
  // mirrors the commit-focus effect above, but targets a branch label's (x, y) instead of a
  // commit's. Reuses branchHoverRef (assigned from setBranchHover in the wheel/drag effect
  // above) rather than a one-off highlight of just the label box: what a "fly to branch" click
  // (BranchFilter.tsx) actually wants to show is the branch's whole path landing in view, the
  // same highlight already used for hovering it directly in the graph — not a separate visual.
  useEffect(() => {
    if (!focusedBranchName || !containerRef.current || !svgRef.current || !gRef.current) return;
    const label = layoutRef.current.branchLabels.find((l) => l.name === focusedBranchName);
    if (!label) return;

    const el = containerRef.current;
    const TARGET_K = 1.0;
    scaleRef.current = TARGET_K;
    const { svgWidth, svgHeight } = layoutRef.current;
    const w = Math.round(svgWidth  * TARGET_K);
    const h = Math.round(svgHeight * TARGET_K);
    svgRef.current.setAttribute('width',  String(w));
    svgRef.current.setAttribute('height', String(h));
    svgRef.current.style.width  = `${w}px`;
    svgRef.current.style.height = `${h}px`;
    gRef.current.setAttribute('transform', `scale(${TARGET_K})`);

    el.scrollTo({
      left: label.x * TARGET_K - el.clientWidth  / 2,
      top:  label.y * TARGET_K - el.clientHeight / 2,
      behavior: 'smooth',
    });

    branchHoverRef.current?.(focusedBranchName);
    const timer = setTimeout(() => branchHoverRef.current?.(null), 3000);
    return () => clearTimeout(timer);
  }, [focusedBranchName]);

  return (
    <>
      <div className="git-graph-card">
        {packagesTruncated && (
          <div
            role="note"
            style={{ position: 'absolute', top: 8, right: 8, zIndex: 5, fontSize: 12, padding: '2px 8px', borderRadius: 4, background: 'var(--bg-elev, var(--bg))', border: '1px solid var(--border, currentColor)', opacity: 0.9 }}
            title="L'index des packages dépasse 5 pages de 100 : certains commits ayant produit un package peuvent ne pas avoir d'anneau."
          >
            Marqueurs de packages possiblement incomplets
          </div>
        )}
        <div
          ref={containerRef}
          className="git-graph-viewport"
        >
          <svg
            ref={svgRef}
            width={layout.svgWidth}
            height={layout.svgHeight}
            style={{ display: 'block' }}
          >
            <defs>
              {/* Geometry driven by the ARROW_* constants above renderGraph — not hardcoded here too,
                  that's exactly what let this drift out of sync with ARROW_MARKER_LEN twice before. */}
              <marker id="graph-arrow" markerWidth={ARROW_TIP_X} markerHeight={ARROW_HEIGHT}
                refX={ARROW_REF_X} refY={ARROW_HEIGHT / 2}
                orient="auto" markerUnits="userSpaceOnUse">
                <polygon
                  points={`${ARROW_BASE_X} 0, ${ARROW_TIP_X} ${ARROW_HEIGHT / 2}, ${ARROW_BASE_X} ${ARROW_HEIGHT}`}
                  fill="context-stroke"
                />
              </marker>
            </defs>
            <g ref={gRef} />
          </svg>
        </div>
        {/* Boutons +/−/ajuster : pensés pour les utilisateurs sans souris (trackpad seul), pour qui
            le zoom molette+Ctrl existant n'est ni découvrable ni pratique. Appellent directement
            les fonctions exposées par l'effet wheel/drag via zoomControlsRef (voir plus haut) —
            même raccourci clavier +/−/0 en parallèle, voir handleKeyDown. */}
        <div className="git-graph-zoom-controls">
          <button type="button" title="Zoomer (+)" onClick={() => zoomControlsRef.current?.zoomIn()}>
            <ZoomInIcon />
          </button>
          <button type="button" title="Dézoomer (-)" onClick={() => zoomControlsRef.current?.zoomOut()}>
            <ZoomOutIcon />
          </button>
          <button type="button" title="Ajuster à l'écran (0)" onClick={() => zoomControlsRef.current?.fitToView()}>
            <FitToViewIcon />
          </button>
        </div>
        <div className="git-graph-minimap-bar">
          <canvas
            ref={minimapCanvasRef}
            className="git-graph-minimap"
            height={76}
            title="Vue d'ensemble — cliquer ou glisser pour naviguer"
            onClick={handleMinimapInteract}
            onMouseMove={(e) => { if (e.buttons === 1) handleMinimapInteract(e); }}
          />
        </div>
      </div>
      {tooltip && (
        <div
          ref={tooltipRef}
          className="commit-tooltip"
          style={{ left: tooltipLeft, top: tooltipTop }}
          onMouseEnter={() => {
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
          }}
          // Épinglée : ignore la sortie de la tooltip elle-même aussi — sinon la quitter pour
          // aller cliquer ailleurs (ou y revenir) la refermerait immédiatement, ce qui viderait le
          // pin de son intérêt.
          onMouseLeave={() => { if (!tooltip.pinned) setTooltip(null); }}
        >
          <div className="commit-tooltip-meta">
            <code>{tooltip.sha.slice(0, 8)}</code>
            <span>{tooltip.authorName}</span>
            <span>{formatDate(tooltip.authoredAt)}</span>
            <div className="commit-tooltip-actions">
              <button
                className="commit-tooltip-copy"
                title={tooltipCopied ? 'Copié !' : 'Copier le SHA complet'}
                onClick={() => {
                  void navigator.clipboard.writeText(tooltip.sha);
                  setTooltipCopied(true);
                  setTimeout(() => setTooltipCopied(false), 1500);
                }}
              >
                {tooltipCopied ? <CopiedIcon /> : <CopyIcon />}
              </button>
              <button
                className="commit-tooltip-pin"
                title={tooltip.pinned ? 'Désépingler' : 'Épingler (reste ouverte pour cliquer dedans)'}
                onClick={() => setTooltip((prev) => (prev ? { ...prev, pinned: !prev.pinned } : prev))}
              >
                <PinIcon pinned={tooltip.pinned} />
              </button>
            </div>
          </div>
          <pre>{renderCommitBody(tooltip.text, jiraBaseUrl)}</pre>
          {/* key = sha : le délai de survol (voir CommitCiSection) repart à zéro quand le curseur passe
              à un autre commit sans que la tooltip se ferme entre-temps. */}
          <CommitCiSection
            key={tooltip.sha}
            repoId={repoId}
            sha={tooltip.sha}
            pinned={tooltip.pinned}
            onPin={() => setTooltip((prev) => (prev ? { ...prev, pinned: true } : prev))}
            // La tooltip (z-index 1000) passerait au-dessus de la modal (999) : on la ferme avant de l'ouvrir.
            onConfigure={onConfigureCi ? () => { setTooltip(null); onConfigureCi(); } : undefined}
          />
        </div>
      )}
    </>
  );
}
